import { Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import ReduxProvider from '@/store/provider';
import { RoleProvider } from '@/contexts/role-context';
import { QueryProvider } from '@/providers/QueryProvider';
import { AuthProvider } from '@/providers/AuthProvider';
import { useAuth } from '@/providers/AuthProvider';
import AppLayout from '@/components/layout/app-layout';
import CoreGuard from '@/components/guards/core-guard';
import { ProtectedRoute } from '@/components/protection/protected-route';
import { updateDocumentDirection } from '@/i18n/config';
import DirectionProvider from '@/components/layout/direction-provider';
import RTLWrapper from '@/components/layout/rtl-wrapper';
import { GlobalErrorHandler } from '@/components/errors/global-error-handler';

// Page imports
import LoginPage from '@/pages/auth/login/page';
import DashboardPage from '@/pages/dashboard/page';
import PropertiesPage from '@/pages/properties/page';
import PropertyCreateStep1Page from '@/pages/properties/create/step-1/page';
import PropertyCreateStep2Page from '@/pages/properties/create/step-2/page';
import PropertyCreateStep3Page from '@/pages/properties/create/step-3/page';
import EditPropertyPage from '@/pages/properties/edit/page';
import PropertyDetailsPage from '@/pages/properties/[id]/page';
import MidStandardPage from '@/pages/properties/[id]/mid-standard/[assessmentId]/page';
import AssessmentsPage from '@/pages/assessments/page';
import CreateAssessmentPage from '@/pages/assessments/create-assessment/page';
import HousekeepingInspectionPage from '@/pages/assessments/perform/[id]/page';
import TasksPage from '@/pages/tasks/page';
import UsersPage from '@/pages/users/page';
import CreateUserPage from '@/pages/users/create-user/page';
import EditUserPage from '@/pages/users/edit-user/[id]/page';
import AdminTemplatesPage from '@/pages/admin/templates/page';
import TemplateDetailPage from '@/pages/admin/templates/[id]/page';
import AddStandardsPage from '@/pages/admin/templates/[id]/add-standards/page';
import EditStandardsPage from '@/pages/admin/templates/[id]/edit-standards/page';
import GuidelinesPage from '@/pages/guidelines/page';


// Protected route wrapper component
function ProtectedRoutes() {
  const { isAuthenticated, user, logout } = useAuth();
  const location = window.location.pathname;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Manager redirection: Only allow property detail and missed standards pages
  if (user && user.roles.includes('manager')) {
    const propertyId = user.property?.id;
    
    // If manager doesn't have a property assigned, show error
    if (!propertyId) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center p-8">
            <h2 className="text-2xl font-bold mb-4">No Property Assigned</h2>
            <p className="text-muted-foreground mb-6">Please contact your administrator to assign a property to your account.</p>
            <button
              onClick={() => logout()}
              className="px-6 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      );
    }

    // Allow manager to access:
    // 1. Their property detail page: /properties/{propertyId}
    // 2. Missed standards page: /properties/{propertyId}/mid-standard/{assessmentId}
    // 3. Assessment perform page (view-only): /assessments/perform/{id}
    // 4. Guidelines page: /guidelines
    const allowedPaths = [
      `/properties/${propertyId}`,
      `/properties/${propertyId}/mid-standard`,
      `/assessments/perform`,
      `/guidelines`,
    ];
    
    const isAllowedPath = allowedPaths.some(path => location.startsWith(path));
    
    // If manager is trying to access a page they're not allowed to see, redirect to their property
    if (!isAllowedPath) {
      return <Navigate to={`/properties/${propertyId}`} replace />;
    }
  }

  // Auditor redirection: Only allow assessments pages
  if (user && user.roles.includes('auditor')) {
    // Allow auditor to access:
    // 1. Assessments list page: /assessments
    // 2. Perform assessment page: /assessments/perform/{id}
    // 3. Guidelines page: /guidelines
    const allowedPaths = [
      '/assessments',
      '/assessments/perform',
      '/guidelines',
    ];
    
    const isAllowedPath = allowedPaths.some(path => location.startsWith(path));
    
    // If auditor is trying to access a page they're not allowed to see, redirect to assessments
    if (!isAllowedPath) {
      return <Navigate to="/assessments" replace />;
    }
  }

  return <AppLayout />;
}

function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    // Initialize document direction based on current language
    updateDocumentDirection(i18n.language);
  }, [i18n.language]);

  return (
    <DirectionProvider>
      <RTLWrapper>
        <div className={cn('font-body antialiased')}>
          <ReduxProvider>
            <QueryProvider>
              <AuthProvider>
                <GlobalErrorHandler>
                  <CoreGuard>
                    <RoleProvider>
                      <Routes>
                        {/* Public routes */}
                        <Route path="/login" element={<LoginPage />} />

                        {/* Protected routes with layout */}
                        <Route path="/*" element={<ProtectedRoutes />}>
                          <Route
                            path="dashboard"
                            element={
                              <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                                <DashboardPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="properties"
                            element={
                              <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                                <PropertiesPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="properties/create"
                            element={
                              <ProtectedRoute allowedRoles={['super_admin']}>
                                <Navigate to="/properties/create/1" replace />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="properties/create/1"
                            element={
                              <ProtectedRoute allowedRoles={['super_admin']}>
                                <PropertyCreateStep1Page />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="properties/create/2"
                            element={
                              <ProtectedRoute allowedRoles={['super_admin']}>
                                <PropertyCreateStep2Page />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="properties/create/3"
                            element={
                              <ProtectedRoute allowedRoles={['super_admin']}>
                                <PropertyCreateStep3Page />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="properties/edit/:id"
                            element={
                              <ProtectedRoute allowedRoles={['super_admin']}>
                                <EditPropertyPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="properties/:id"
                            element={
                              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'manager']}>
                                <PropertyDetailsPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="properties/:id/mid-standard/:assessmentId"
                            element={
                              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'manager']}>
                                <MidStandardPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="assessments"
                            element={
                              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'auditor']}>
                                <AssessmentsPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="assessments/create-assessment"
                            element={
                              <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                                <CreateAssessmentPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="assessments/perform/:id"
                            element={
                              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'manager', 'auditor']}>
                                <HousekeepingInspectionPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route 
                            path="tasks" 
                            element={
                              <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                                <TasksPage />
                              </ProtectedRoute>
                            } 
                          />
                          <Route 
                            path="users" 
                            element={
                              <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                                <UsersPage />
                              </ProtectedRoute>
                            } 
                          />
                          <Route
                            path="users/create-user"
                            element={
                              <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                                <CreateUserPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="users/edit-user/:id"
                            element={
                              <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                                <EditUserPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="admin/templates"
                            element={
                              <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                                <AdminTemplatesPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="admin/templates/:id"
                            element={
                              <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                                <TemplateDetailPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="admin/templates/:id/add-standards"
                            element={
                              <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                                <AddStandardsPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="admin/templates/:id/edit-standards"
                            element={
                              <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                                <EditStandardsPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="guidelines"
                            element={
                              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'manager', 'auditor']}>
                                <GuidelinesPage />
                              </ProtectedRoute>
                            }
                          />
                          {/* Redirect to dashboard by default */}
                          <Route
                            path="*"
                            element={<Navigate to="/dashboard" replace />}
                          />
                        </Route>

                        {/* Root redirect */}
                        <Route
                          path="/"
                          element={<Navigate to="/login" replace />}
                        />
                      </Routes>
                      <Toaster />
                    </RoleProvider>
                  </CoreGuard>
                </GlobalErrorHandler>
              </AuthProvider>
            </QueryProvider>
          </ReduxProvider>
        </div>
      </RTLWrapper>
    </DirectionProvider>
  );
}

export default App;
